import { computed } from 'vue'
import { useI18n as useVueI18n } from 'vue-i18n'
import {
  changeLocale,
  getCurrentLocale,
  SUPPORTED_LOCALES,
  LOCALE_INFO,
  type Locale
} from '@/i18n'

export function useI18n() {
  const { t, locale, availableLocales } = useVueI18n()

  // Current locale as computed ref
  const currentLocale = computed(() => getCurrentLocale())

  // Available locales with display information
  const localeOptions = computed(() =>
    SUPPORTED_LOCALES.map(localeCode => ({
      value: localeCode,
      code: localeCode.toUpperCase(),
      label: LOCALE_INFO[localeCode].nativeName,
      flag: LOCALE_INFO[localeCode].flag,
      name: LOCALE_INFO[localeCode].name
    }))
  )

  // Check if current locale is RTL (for future Arabic support)
  const isRTL = computed(() => {
    const rtlLocales = ['ar', 'he', 'fa'] // Arabic, Hebrew, Persian
    return rtlLocales.includes(currentLocale.value)
  })

  // Format currency according to locale
  const formatCurrency = (amount: number, currency = 'MGA') => {
    const locale = getCurrentLocale()

    try {
      // Create formatter based on locale
      const formatter = new Intl.NumberFormat(
        locale === 'fr' ? 'fr-FR' : 'en-US',
        {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }
      )

      return formatter.format(amount)
    } catch (error) {
      // Fallback formatting
      const symbol = currency === 'MGA' ? 'Ar' : currency
      return `${symbol} ${amount.toLocaleString(locale === 'fr' ? 'fr-FR' : 'en-US')}`
    }
  }

  // Format date according to locale
  const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const locale = getCurrentLocale()
    const dateObj = typeof date === 'string' ? new Date(date) : date

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }

    return dateObj.toLocaleDateString(
      locale === 'fr' ? 'fr-FR' : 'en-US',
      { ...defaultOptions, ...options }
    )
  }

  // Format time according to locale
  const formatTime = (date: Date | string, options?: Intl.DateTimeFormatOptions) => {
    const locale = getCurrentLocale()
    const dateObj = typeof date === 'string' ? new Date(date) : date

    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: locale === 'en' // Use 12-hour format for English, 24-hour for French
    }

    return dateObj.toLocaleTimeString(
      locale === 'fr' ? 'fr-FR' : 'en-US',
      { ...defaultOptions, ...options }
    )
  }

  // Format number according to locale
  const formatNumber = (number: number, options?: Intl.NumberFormatOptions) => {
    const locale = getCurrentLocale()
    return number.toLocaleString(
      locale === 'fr' ? 'fr-FR' : 'en-US',
      options
    )
  }

  // Switch to a specific locale with authentication awareness
  const switchLocale = async (newLocale: Locale) => {
    try {
      // Dynamic import to avoid circular dependency
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()

      if (authStore.isAuthenticated) {
        // If user is logged in, update both local and remote preferences
        const success = await authStore.updateUserLanguage(newLocale)
        if (success) {
          // Only change locale if API call succeeded
          changeLocale(newLocale)
        } else {
          // Still change locally as fallback
          changeLocale(newLocale)
        }
      } else {
        // If not logged in, just change locally
        changeLocale(newLocale)
      }
    } catch (error) {
      // Silently fail and change locale locally only
      // Error logging handled by auth store
      if (import.meta.env.DEV) {
        console.warn('Failed to update language preference:', error)
      }
      changeLocale(newLocale)
    }
  }

  // Get translation with type safety
  const translate = (key: string, values?: Record<string, any>) => {
    return t(key, values || {})
  }

  return {
    // Translation function
    t: translate,
    
    // Locale management
    locale,
    currentLocale,
    availableLocales,
    localeOptions,
    switchLocale,
    isRTL,
    
    // Formatting functions
    formatCurrency,
    formatDate,
    formatTime,
    formatNumber
  }
}